/*
  Text → Checklist tool. Paste prose, get actionable items. Send all to
  either To-Do or Shopping list in one click.
*/
import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, Check, ArrowUpRight, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { phase3Api } from "../lib/phase3Api";
import { todos, shopping } from "../lib/api";

export default function TextToChecklistPage() {
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState({});
  const [done, setDone] = useState(null);

  async function extract() {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const res = await phase3Api.textToChecklist(text.trim());
      setItems(res.items || []);
      setAccepted(Object.fromEntries((res.items || []).map((_, i) => [i, true])));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't extract"); }
    finally { setBusy(false); }
  }

  async function sendTo(kind) {
    const selected = items.filter((_, i) => accepted[i]);
    if (selected.length === 0) { toast.error("Nothing selected"); return; }
    setBusy(true);
    try {
      if (kind === "todo") {
        for (const it of selected) await todos.create({ title: it.title, priority: "medium", category: "general" });
      } else {
        for (const it of selected) await shopping.create({ name: it.title, category: "general" });
      }
      setDone(kind);
      toast.success(`Added ${selected.length} to ${kind === "todo" ? "To-Do" : "Shopping"}`);
    } catch { toast.error("Couldn't add"); }
    finally { setBusy(false); }
  }

  return (
    <div className="animate-fade-up max-w-2xl">
      <span className="label-caps">Quick tool</span>
      <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Text → Checklist.</h1>
      <p className="mt-2 text-sm sm:text-base" style={{ color: "var(--text-muted)" }}>
        Paste any messy text — meeting notes, an email, your brain dump. We'll return clean action items.
      </p>

      <div className="card-tactile rounded-2xl p-5 mt-6 space-y-3">
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Paste anything…"
          data-testid="t2c-input"
          rows={8}
          className="w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] text-sm focus:outline-none focus:border-[color:var(--primary)]"
          style={{ borderColor: "var(--border-subtle)" }} />
        <button onClick={extract} disabled={busy || !text.trim()}
          data-testid="t2c-extract"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--primary)" }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Extract checklist
        </button>
      </div>

      {items.length > 0 && (
        <div className="card-tactile rounded-2xl p-5 mt-5" data-testid="t2c-results">
          <div className="label-caps mb-2 flex items-center gap-1"><ListChecks className="h-3 w-3" /> {items.length} items</div>
          <ul className="space-y-1.5">
            {items.map((it, i) => (
              <li key={i}>
                <label className="flex items-start gap-2 p-2 rounded hover:bg-[color:var(--surface-2)] cursor-pointer">
                  <input type="checkbox" checked={!!accepted[i]}
                    onChange={(e) => setAccepted(a => ({ ...a, [i]: e.target.checked }))}
                    className="mt-1 accent-[color:var(--primary)]" />
                  <span className="text-sm">{it.title}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => sendTo("todo")} data-testid="t2c-to-todo"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              disabled={busy}
              style={{ background: "var(--primary)" }}>
              <Send className="h-4 w-4" /> Send to To-Do
            </button>
            <button onClick={() => sendTo("shopping")} data-testid="t2c-to-shopping"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold border hover:bg-[color:var(--surface-2)] disabled:opacity-50"
              disabled={busy}
              style={{ borderColor: "var(--border-subtle)" }}>
              <Send className="h-4 w-4" /> Send to Shopping
            </button>
            {done && (
              <Link to={done === "todo" ? "/todo" : "/shopping"}
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold"
                style={{ color: "var(--primary-ink)" }}>
                <Check className="h-3 w-3" /> Open {done === "todo" ? "To-Do" : "Shopping"} <ArrowUpRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
